import React, { useState, useEffect } from "react";
import { ArrowRight, Phone, Star, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { COMPANY, STATS } from "../lib/constants";

const HERO_IMAGES = [
  "https://static.prod-images.emergentagent.com/jobs/55965fb7-f09c-47b4-bb31-d6b610b36aa9/images/2b4285375fb51253ba1ca082924e2a835281f5517b850b786772bb9dca072d2f.png",
  "https://customer-assets.emergentagent.com/job_tire-commerce/artifacts/wzzla333_image.png",
  "https://static.prod-images.emergentagent.com/jobs/55965fb7-f09c-47b4-bb31-d6b610b36aa9/images/f595a04357fde7dbf07475623fe11411152ddea06c322877dfe36f03cb53096f.png",
  "https://customer-assets.emergentagent.com/job_tire-commerce/artifacts/cthm9e2e_image.png",
  "https://images.unsplash.com/photo-1578583445429-b4ba53d7045f?auto=format&fit=crop&w=1920&q=80",
  "https://customer-assets.emergentagent.com/job_tire-commerce/artifacts/7h6basxr_image.png",
  "https://customer-assets.emergentagent.com/job_tire-commerce/artifacts/k77xokt6_image.png",
  "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=1920&q=80",
];

export const Hero = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % HERO_IMAGES.length);
    }, 5500);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      data-testid="hero-section"
      id="home"
      className="relative min-h-[88vh] flex items-center overflow-hidden"
    >
      {/* Background slideshow */}
      {HERO_IMAGES.map((src, i) => (
        <div
          key={src}
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center transition-opacity duration-[1800ms] ease-in-out"
          style={{
            backgroundImage: `url(${src})`,
            opacity: i === activeIndex ? 1 : 0,
            filter: "brightness(0.55) saturate(1)",
            zIndex: 0,
          }}
        />
      ))}
      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0E2452] via-[#0E2452]/80 to-[#FF5A00]/20 z-[1]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0E2452] via-transparent to-transparent z-[1]" />

      {/* Slide indicators */}
      <div className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-2">
        {HERO_IMAGES.map((src, i) => (
          <button
            key={src}
            data-testid={`hero-slide-dot-${i}`}
            onClick={() => setActiveIndex(i)}
            aria-label={`Imagem ${i + 1}`}
            className={`h-1 transition-all duration-300 ${
              i === activeIndex ? "w-8 bg-[#FF5A00]" : "w-4 bg-white/30 hover:bg-white/60"
            }`}
          />
        ))}
      </div>
      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-32 pb-14">
        <div className="max-w-4xl">
          <div
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 border border-[#FF5A00]/40 bg-[#FF5A00]/10 px-3.5 py-1.5 mb-5 rounded-full"
          >
            <Star className="w-4 h-4 text-[#FF5A00] fill-[#FF5A00]" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#FF5A00] font-display font-semibold">
              Nº 1 em {COMPANY.city}
            </span>
          </div>

          <h1 data-testid="hero-title" className="heading-xl text-white mb-4">
            Pneus que
            <br />
            <span className="text-[#FF5A00]">não falham.</span>
            <br />
            <span className="text-white/70">Serviço</span> que impressiona.
          </h1>

          <p
            data-testid="hero-subtitle"
            className="text-base md:text-lg text-[#E0E0E0] leading-relaxed max-w-xl mb-7 font-body"
          >
            {COMPANY.tagline} Oficina completa com equipamento de última geração,
            técnicos certificados e as melhores marcas do mercado.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-10">
            <Link
              data-testid="hero-cta-quote"
              to="/orcamento"
              className="btn-primary group"
            >
              Pedir Orçamento
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              data-testid="hero-cta-phone"
              href={COMPANY.landlineHref}
              className="btn-outline group"
            >
              <Phone className="w-5 h-5" />
              Ligar Agora
            </a>
          </div>

          {/* Stats */}
          <div
            data-testid="hero-stats"
            className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 max-w-4xl"
          >
            {STATS.map((s) => (
              <div
                key={s.text}
                className="rounded-2xl border border-[#2F568F] p-3.5 bg-[#0E2452]/60 backdrop-blur-sm flex items-start gap-3"
              >
                <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center bg-[#FF5A00] text-black rounded-full">
                  <Check className="w-3.5 h-3.5" strokeWidth={3} />
                </span>
                <div className="font-display font-semibold text-xs md:text-sm text-white leading-snug">
                  {s.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator removed */}
    </section>
  );
};

export default Hero;
